import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { EN_EAGER_BUNDLE } from '@/lib/i18n/bundles/en-eager';
import {
  loadEnglishNamespace,
  loadLanguageBundle,
} from '@/lib/i18n/lazy-load-language';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/lib/i18n/language';
import { NAMESPACES, type LazyNamespace } from '@/lib/i18n/resources';
import { reloadForStaleChunk } from '@/lib/stale-chunk';
import type { UiLanguage } from '@/types/generated/enums';

export const i18n = createInstance();

// Only the default language's eager namespaces are bundled, so init stays synchronous.
void i18n.use(initReactI18next).init({
  resources: { [DEFAULT_LANGUAGE]: EN_EAGER_BUNDLE },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: [...NAMESPACES],
  defaultNS: 'common',
  keySeparator: false,
  // Defaults to true, which makes a blank translation a hit and renders nothing.
  returnEmptyString: false,
  interpolation: { escapeValue: false },
  initAsync: false,
});

type LoadOutcome = { loaded: true } | { loaded: false; error: unknown };

// Cached before any await, so racing callers share one attempt and one Sentry report.
const languageLoads = new Map<UiLanguage, Promise<LoadOutcome>>();

const loadOnce = (language: UiLanguage): Promise<LoadOutcome> => {
  const cached = languageLoads.get(language);
  if (cached) return cached;
  const attempt = (async (): Promise<LoadOutcome> => {
    try {
      const bundle = await loadLanguageBundle(language);
      for (const [namespace, resources] of Object.entries(bundle)) {
        i18n.addResourceBundle(language, namespace, resources);
      }
      return { loaded: true };
    } catch (error) {
      Sentry.captureException(error);
      return { loaded: false, error };
    }
  })();
  languageLoads.set(language, attempt);
  return attempt;
};

// Dropped once settled, so a failed chunk is fetched again by the route error fallback's Retry.
const namespaceLoads = new Map<LazyNamespace, Promise<void>>();

const loadNamespace = (namespace: LazyNamespace): Promise<void> => {
  if (i18n.hasResourceBundle(DEFAULT_LANGUAGE, namespace)) {
    return Promise.resolve();
  }
  const pending = namespaceLoads.get(namespace);
  if (pending) return pending;
  const attempt = (async (): Promise<void> => {
    try {
      const bundle = await loadEnglishNamespace(namespace);
      i18n.addResourceBundle(DEFAULT_LANGUAGE, namespace, bundle);
    } finally {
      namespaceLoads.delete(namespace);
    }
  })();
  namespaceLoads.set(namespace, attempt);
  return attempt;
};

// Route loaders await this: i18next renders a missing namespace's raw key rather than waiting.
export const loadNamespaces = async (
  ...namespaces: LazyNamespace[]
): Promise<void> => {
  await Promise.all(namespaces.map(loadNamespace));
};

// Registered by query-client.ts; importing it here closed a cycle back through billing-gate.
const transitionListeners = new Set<() => void>();

export const onLanguageTransition = (listener: () => void): void => {
  transitionListeners.add(listener);
};

const settleLanguage = (language: UiLanguage, loaded: boolean): boolean => {
  const resolved = loaded ? language : DEFAULT_LANGUAGE;
  // i18n.language is the "did it really change" record, so every route's beforeLoad re-calling this in the same language never re-invalidates.
  const isRealTransition = i18n.language !== resolved;
  void i18n.changeLanguage(resolved);
  if (typeof document !== 'undefined') document.documentElement.lang = resolved;
  if (isRealTransition) for (const listener of transitionListeners) listener();
  return resolved === language;
};

const isLoaded = (language: UiLanguage): boolean =>
  i18n.hasResourceBundle(language, 'common');

// Switches before its first await once the bundle is in; false when English took over. Never reloads: beforeLoad re-runs on a hover.
export const applyLanguage = async (language: UiLanguage): Promise<boolean> => {
  if (isLoaded(language)) return settleLanguage(language, true);
  return settleLanguage(language, (await loadOnce(language)).loaded);
};

// The picker's deliberate switch spends the build's one reload; it settles so a cancelled one can toast.
export const switchLanguage = async (
  language: UiLanguage,
): Promise<boolean> => {
  if (isLoaded(language)) return settleLanguage(language, true);
  const outcome = await loadOnce(language);
  if (!outcome.loaded) reloadForStaleChunk(outcome.error);
  return settleLanguage(language, outcome.loaded);
};
